import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, CheckCircle2, Lock } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { API } from "../lib/api";

// Simple front-line admin UI for owner to upload/replace the book PDF.
// Password is checked server-side. Default is "sufi-sarkar-admin" (change in backend .env BOOK_ADMIN_PASSWORD).

export default function AdminBook() {
  useEffect(() => { document.title = "Admin — Upload Book"; }, []);
  const [password, setPassword] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!file) { toast.error("Choose a PDF file."); return; }
    if (file.type !== "application/pdf") { toast.error("Only PDF files are allowed."); return; }
    if (file.size > 25 * 1024 * 1024) { toast.error("File must be 25 MB or smaller."); return; }
    setLoading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("password", password);
      const res = await fetch(`${API}/book/upload`, { method: "POST", body: form });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Upload failed");
      toast.success("Book uploaded successfully!");
      setDone(true);
    } catch (err) {
      toast.error(err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-[80vh] pattern-bg flex items-center" data-testid="admin-book-page">
      <div className="container-page max-w-xl">
        <div className="card-soft p-10 reveal in">
          <div className="flex items-center gap-3 mb-6">
            <Lock className="text-gold" size={22} />
            <h1 className="font-serif text-3xl text-emerald">Upload Book PDF</h1>
          </div>
          {done ? (
            <div className="text-center py-10" data-testid="admin-success">
              <CheckCircle2 className="text-gold mx-auto" size={48} />
              <p className="font-serif text-2xl text-emerald mt-4">Book updated</p>
              <p className="text-charcoal-muted mt-2">The new PDF is now live at <a href="/the-book" className="text-gold underline">/the-book</a>.</p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="admin-form">
              <div>
                <Label className="uppercase tracking-[0.18em] text-xs text-charcoal-muted">Admin Password</Label>
                <Input type="password" value={password} onChange={e => setPassword(e.target.value)} className="mt-2 bg-transparent border-gold/40 focus-visible:ring-gold" data-testid="admin-password" />
              </div>
              <div>
                <Label className="uppercase tracking-[0.18em] text-xs text-charcoal-muted">Book PDF (max 25 MB)</Label>
                <Input type="file" accept="application/pdf" onChange={e => setFile(e.target.files[0])} className="mt-2 bg-transparent border-gold/40 focus-visible:ring-gold cursor-pointer" data-testid="admin-file" />
                {file && <p className="text-xs text-charcoal-muted mt-2">{file.name} · {(file.size / 1024 / 1024).toFixed(2)} MB</p>}
              </div>
              <button type="submit" disabled={loading} className="btn-gold w-full disabled:opacity-60" data-testid="admin-upload">
                <Upload size={16} className="mr-2" />
                {loading ? "Uploading..." : "Upload & Publish"}
              </button>
              <p className="text-xs text-charcoal-muted text-center">The file replaces the current book and is served immediately at <code>/book/hazrat-book.pdf</code>.</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
